import React, { useEffect, useMemo, useState } from 'react';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';
import { EmptyState } from '../common/EmptyState';
import { Tag } from '../common/Tag';
import { fmtDate, relTime } from '../../utils/helpers';
import DocumentUploadModal from './DocumentUploadModal';
import DocumentDetailPanel from './DocumentDetailPanel';
import DocumentPreviewModal from './DocumentPreviewModal';
import { studyFolderAndDocumentServices } from '../../APIServices/studyFolderAndDocumentServices';

function StudyDetailPage({ studyId, onBack }) {
  const { state, audit } = useData();
  const toast = useToast();
  const study = state.studies.find((s) => s.id === studyId);

  const [activeTab, setActiveTab] = useState('overview');
  const [activeFolder, setActiveFolder] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [previewDocId, setPreviewDocId] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState([]);
  const [filterQuery, setFilterQuery] = useState('');
  const [treeLoading, setTreeLoading] = useState(false);
  const [folderRows, setFolderRows] = useState([]);
  const [deletedFolderCount, setDeletedFolderCount] = useState(0);
  const [folderActionLoading, setFolderActionLoading] = useState(false);

  // Folder Management State
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [folderModalMode, setFolderModalMode] = useState('add'); // 'add', 'edit', 'delete'
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderForm, setFolderForm] = useState({ code: '', name: '', desc: '', parentId: null });

  const documentRows = useMemo(() => {
    return (folderRows || [])
      .filter((row) => row.type === 'file')
      .map((row) => ({
        id: row._id,
        _id: row._id,
        uniqueId: row.uniqueId,
        studyId: row.studyId,
        folder: row.parentId || '',
        name: row.name || row.fileName || '',
        type: row.documentType || 'Document',
        version: row.version || '1.0',
        status: row.status || 'draft',
        confidentiality: row.confidentiality || 'Confidential',
        tags: row.documentTag || [],
        fileUrl: row.fileUrl || '',
        fileName: row.fileName || row.name || '',
        fileSize: row.fileSize || 0,
        mimeType: row.mimeType || '',
        size: row.fileSize ? `${(row.fileSize / (1024 * 1024)).toFixed(2)} MB` : '—',
        parentFolderCode: row.parentFolderCode || '',
        parentFolderName: row.parentFolderName || '',
        deleted: !!row.deleted,
        deletedDT: row.deletedDT,
        updatedAt: row.updatedAt,
        updatedBy: row.updatedBy,
      }));
  }, [folderRows]);

  const resolvedStudyId = study?.studyId || studyId;
  const allFolders = useMemo(() => {
    return (folderRows || []).filter((row) => row.type === 'folder').map((row) => ({
      id: row._id,
      uniqueId: row.uniqueId,
      parentId: row.parentId || null,
      code: row.code || '',
      name: row.name || '',
      desc: row.description || '',
      status: row.status,
      deleted: !!row.deleted,
    }));
  }, [folderRows]);

  const activeFolderNode = useMemo(
    () => allFolders.find((f) => f.uniqueId === activeFolder) || null,
    [allFolders, activeFolder]
  );

  const folderById = useMemo(() => {
    const map = {};
    allFolders.forEach((folder) => {
      map[folder.uniqueId] = folder;
    });
    return map;
  }, [allFolders]);

  const getDocumentFolderLabel = (doc) => {
    if (doc.parentFolderName) {
      return doc.parentFolderCode
        ? `${doc.parentFolderCode}. ${doc.parentFolderName}`
        : doc.parentFolderName;
    }

    const mappedFolder = folderById[doc.folder];
    if (!mappedFolder) return '—';
    return `${mappedFolder.code}. ${mappedFolder.name}`;
  };

  const selectedFolderIds = useMemo(() => {
    if (!activeFolderNode?.uniqueId) return [];

    const ids = [activeFolderNode.uniqueId];
    const queue = [activeFolderNode.uniqueId];

    while (queue.length > 0) {
      const currentId = queue.shift();
      const children = allFolders.filter((folder) => folder.parentId === currentId);

      children.forEach((child) => {
        ids.push(child.uniqueId);
        queue.push(child.uniqueId);
      });
    }

    return ids;
  }, [activeFolderNode, allFolders]);

  const docsInFolder = useMemo(
    () => documentRows.filter((d) => {
      if (!activeFolderNode) return false;
      const inFolder = selectedFolderIds.includes(d.folder);
      return inFolder && (showDeleted ? d.deleted : !d.deleted);
    }),
    [documentRows, activeFolderNode, selectedFolderIds, showDeleted]
  );

  const deletedDocuments = useMemo(
    () => documentRows.filter((d) => d.deleted),
    [documentRows]
  );

  const visibleFolders = useMemo(
    () => (showDeleted ? allFolders.filter((f) => f.deleted) : allFolders),
    [allFolders, showDeleted]
  );

  const folderCounts = useMemo(() => {
    const m = {};
    allFolders.forEach((f) => { m[f.uniqueId] = 0; });

    const parentMap = {};
    allFolders.forEach((folder) => {
      parentMap[folder.uniqueId] = folder.parentId || null;
    });

    documentRows.filter((d) => !d.deleted).forEach((d) => {
      let currentFolderId = d.folder;
      while (currentFolderId) {
        if (m[currentFolderId] !== undefined) {
          m[currentFolderId] = (m[currentFolderId] || 0) + 1;
        }
        currentFolderId = parentMap[currentFolderId];
      }
    });
    return m;
  }, [documentRows, allFolders]);

  const selectedDoc = documentRows.find((d) => d.id === selectedDocId);
  const previewDoc = documentRows.find((d) => d.id === previewDocId);

  const handleRestore = async (doc) => {
    const shouldRestore = window.confirm(`Are you sure you want to restore "${doc?.name}"?`);
    if (!shouldRestore) return;

    const data = await studyFolderAndDocumentServices.softDeleteRestoreStudyFolderAndDocument({
      id: doc._id,
      uniqueId: doc.uniqueId,
      type: 'restore',
    });

    if (data?.statusCode === 200) {
      audit('DOCUMENT_RESTORE', doc.name, 'Restored from soft delete', { studyId: resolvedStudyId, documentId: doc.id });
      toast(`${doc.name} restored`, 'ok');
      await getStudyFolderAndDocumentData();
    }
  };

  const handlePermanentDeleteDocument = async (doc) => {
    const shouldDelete = window.confirm(`Are you sure you want to permanently delete "${doc?.name}"? This action cannot be undone.`);
    if (!shouldDelete) return;

    const data = await studyFolderAndDocumentServices.softDeleteRestoreStudyFolderAndDocument({
      id: doc._id,
      uniqueId: doc.uniqueId,
      type: 'permanentDelete',
    });

    if (data?.statusCode === 200) {
      audit('DOCUMENT_DELETE_PERMANENT', doc.name, 'Permanently deleted document', { studyId: resolvedStudyId, documentId: doc.id });
      toast(`${doc.name} permanently deleted`, 'ok');
      await getStudyFolderAndDocumentData();
    }
  };

  const toggleFolder = (uniqueId) => {
    setExpandedFolders(prev =>
      prev.includes(uniqueId)
        ? prev.filter(f => f !== uniqueId)
        : [...prev, uniqueId]
    );
  };

  const getFolderIcon = (uniqueId) => {
    if (activeFolder === uniqueId) return 'ti ti-folder-open';
    if (expandedFolders.includes(uniqueId)) return 'ti ti-folder-open';
    return 'ti ti-folder';
  };

  const countDeleted = deletedFolderCount;

  const client = state.clients?.find(c => c.id === study?.clientId);

  // ==================== FOLDER MANAGEMENT FUNCTIONS ====================

  const getStudyFolderAndDocumentData = async (searchText = filterQuery) => {
    if (!resolvedStudyId) return;
    setTreeLoading(true);

    try {
      const data = await studyFolderAndDocumentServices.getStudyFolderAndDocument(
        resolvedStudyId,
        searchText,
        '',
        '',
        showDeleted ? 'deleted' : 'all'
      );

      const isSuccess = data?.statusCode === 200 || data?.foundStatus?.statusCode === 200;
      if (isSuccess) {
        const rows = data.data || [];
        const folderOnlyRows = rows.filter((row) => row.type === 'folder');
        setFolderRows(rows);

        if (folderOnlyRows.length > 0) {
          const rootIds = folderOnlyRows.filter((f) => !f.parentId).map((f) => f.uniqueId);
          setExpandedFolders((prev) => {
            const merged = new Set([...(prev || []), ...rootIds]);
            return Array.from(merged);
          });

          const activeStillExists = folderOnlyRows.some((f) => f.uniqueId === activeFolder);
          if (!activeStillExists) {
            setActiveFolder(rootIds[0] || folderOnlyRows[0]?.uniqueId || null);
          }
        } else {
          setActiveFolder(null);
        }
      } else if (!isSuccess) {
        setFolderRows([]);
        setActiveFolder(null);
      }

      const deletedData = await studyFolderAndDocumentServices.getStudyFolderAndDocument(
        resolvedStudyId,
        '',
        '',
        '',
        'deleted'
      );
      if (deletedData?.statusCode === 200 || deletedData?.foundStatus?.statusCode === 200) {
        setDeletedFolderCount(deletedData?.count || 0);
      } else {
        setDeletedFolderCount(0);
      }
    } catch (error) {
      setFolderRows([]);
      setActiveFolder(null);
      toast('Something went wrong while loading study tree.', 'warn');
    } finally {
      setTreeLoading(false);
    }
  };

  const openAddFolderModal = (parentUniqueId = null) => {
    setFolderModalMode('add');
    setEditingFolder(null);
    setFolderForm({ code: '', name: '', desc: '', parentId: parentUniqueId });
    setShowFolderModal(true);
  };

  const openEditFolderModal = (folder) => {
    setFolderModalMode('edit');
    setEditingFolder(folder);
    setFolderForm({
      code: folder.code,
      name: folder.name,
      desc: folder.desc || '',
      parentId: folder.parentId || null
    });
    setShowFolderModal(true);
  };

  const openDeleteFolderModal = (folder) => {
    setFolderModalMode('delete');
    setEditingFolder(folder);
    setShowFolderModal(true);
  };

  const handleFolderSubmit = async () => {
    if (!folderForm.code || !folderForm.name) {
      toast('Folder code and name are required', 'warn');
      return;
    }

    if (!resolvedStudyId) {
      toast('Study not found for folder operation.', 'warn');
      return;
    }

    setFolderActionLoading(true);

    if (folderModalMode === 'add') {
      const payload = {
        studyId: resolvedStudyId,
        parentId: folderForm.parentId || '',
        name: folderForm.name,
        code: folderForm.code,
        description: folderForm.desc || '',
        type: 'folder',
      };

      try {
        const data = await studyFolderAndDocumentServices.addNewStudyFolderAndDocument(payload);

        if (data?.statusCode === 200) {
          toast(`Folder "${folderForm.name}" created`, 'ok');
          if (folderForm.parentId) {
            setExpandedFolders(prev => [...new Set([...(prev || []), folderForm.parentId])]);
          }
          audit('FOLDER_CREATE', folderForm.code, `Folder created: ${folderForm.name}`, { studyId: resolvedStudyId });
          setShowFolderModal(false);
          setFolderForm({ code: '', name: '', desc: '', parentId: null });
          setEditingFolder(null);
          await getStudyFolderAndDocumentData();
        }
      } finally {
        setFolderActionLoading(false);
      }
    } else if (folderModalMode === 'edit') {
      const payload = {
        uniqueId: editingFolder?.uniqueId,
        name: folderForm.name,
        code: folderForm.code,
        description: folderForm.desc || '',
        parentId: folderForm.parentId || '',
      };

      try {
        const data = await studyFolderAndDocumentServices.updateStudyFolderAndDocument(payload);
        if (data?.statusCode === 200) {
          toast(`Folder "${folderForm.name}" updated`, 'ok');
          audit('FOLDER_EDIT', folderForm.code, `Folder updated: ${folderForm.name}`, { studyId: resolvedStudyId });
          setShowFolderModal(false);
          setFolderForm({ code: '', name: '', desc: '', parentId: null });
          setEditingFolder(null);
          await getStudyFolderAndDocumentData();
        }
      } finally {
        setFolderActionLoading(false);
      }
    } else {
      setFolderActionLoading(false);
    }
  };

  const handleFolderDelete = async () => {
    if (!editingFolder?.id && !editingFolder?.uniqueId) {
      toast('Folder not found.', 'warn');
      return;
    }

    setFolderActionLoading(true);
    try {
      const data = await studyFolderAndDocumentServices.softDeleteRestoreStudyFolderAndDocument({
        id: editingFolder?.id,
        uniqueId: editingFolder?.uniqueId,
        type: 'delete',
      });

      if (data?.statusCode === 200) {
        toast(`Folder "${editingFolder.name}" moved to deleted history`, 'ok');
        audit('FOLDER_DELETE', editingFolder.code, `Folder deleted: ${editingFolder.name}`, { studyId: resolvedStudyId });
        setShowFolderModal(false);
        setEditingFolder(null);
        await getStudyFolderAndDocumentData();
      }
    } finally {
      setFolderActionLoading(false);
    }
  };

  const handleRestoreFolder = async (folder) => {
    const shouldRestore = window.confirm('Are you sure you want to restore this folder?');
    if (!shouldRestore) return;

    const data = await studyFolderAndDocumentServices.softDeleteRestoreStudyFolderAndDocument({
      id: folder?.id,
      uniqueId: folder?.uniqueId,
      type: 'restore',
    });
    if (data?.statusCode === 200) {
      toast(`Folder "${folder?.name}" restored successfully`, 'ok');
      await getStudyFolderAndDocumentData();
    }
  };

  const handlePermanentDeleteFolder = async (folder) => {
    const shouldDelete = window.confirm('Are you sure you want to permanently delete this folder?');
    if (!shouldDelete) return;

    const data = await studyFolderAndDocumentServices.deleteStudyFolderAndDocument({
      uniqueId: folder?.uniqueId,
      studyId: resolvedStudyId,
    });
    if (data?.statusCode === 200) {
      toast(`Folder "${folder?.name}" permanently deleted`, 'ok');
      await getStudyFolderAndDocumentData();
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      getStudyFolderAndDocumentData(filterQuery);
    }, 350);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedStudyId, showDeleted, filterQuery]);

  // ==================== FOLDER MODAL ====================
  const renderFolderModal = () => (
    <div className="modal-backdrop" style={{ display: showFolderModal ? 'flex' : 'none' }} onClick={() => setShowFolderModal(false)}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-head">
          <div>
            <div className="modal-title">
              {folderModalMode === 'add' && 'Add Folder'}
              {folderModalMode === 'edit' && 'Edit Folder'}
              {folderModalMode === 'delete' && 'Delete Folder'}
            </div>
            <div className="xs muted">
              {folderModalMode === 'add' && 'Create a new folder in the study tree'}
              {folderModalMode === 'edit' && 'Update folder details'}
              {folderModalMode === 'delete' && 'Confirm folder deletion'}
            </div>
          </div>
          <button className="icon-btn" onClick={() => setShowFolderModal(false)}>
            <i className="ti ti-x" />
          </button>
        </div>

        <div className="modal-body">
          {folderModalMode === 'delete' ? (
            <div>
              <div className="alert warn" style={{ padding: 12, borderRadius: 6, background: 'var(--warn-soft)', border: '1px solid var(--warn)', marginBottom: 12 }}>
                <i className="ti ti-alert-triangle" />
                <strong>Warning:</strong> This action cannot be undone.
              </div>
              <p>Are you sure you want to delete folder <strong>"{editingFolder?.name}"</strong>?</p>
              <p className="tiny muted">This will only delete the folder. Documents inside this folder will remain in the study but will become unassigned.</p>
            </div>
          ) : (
            <div>
              <div className="field">
                <label className="field-label">Folder Code <span className="req">*</span></label>
                <input
                  className="inp"
                  value={folderForm.code}
                  onChange={(e) => setFolderForm({ ...folderForm, code: e.target.value })}
                  placeholder="e.g., 11, 11-01"
                  disabled={folderModalMode === 'edit'}
                />
                <div className="field-hint">Unique identifier for the folder</div>
              </div>

              <div className="field">
                <label className="field-label">Folder Name <span className="req">*</span></label>
                <input
                  className="inp"
                  value={folderForm.name}
                  onChange={(e) => setFolderForm({ ...folderForm, name: e.target.value })}
                  placeholder="e.g., Clinical Ops"
                />
              </div>

              <div className="field">
                <label className="field-label">Description</label>
                <textarea
                  className="ta"
                  value={folderForm.desc}
                  onChange={(e) => setFolderForm({ ...folderForm, desc: e.target.value })}
                  placeholder="Brief description of folder contents"
                  rows={2}
                />
              </div>

              <div className="field">
                <label className="field-label">Parent Folder</label>
                <select
                  className="sel"
                  value={folderForm.parentId || ''}
                  onChange={(e) => setFolderForm({ ...folderForm, parentId: e.target.value || null })}
                >
                  <option value="">— Root —</option>
                  {allFolders
                    .filter(f => f.uniqueId !== editingFolder?.uniqueId)
                    .map(f => (
                      <option key={f.uniqueId} value={f.uniqueId}>
                        {f.code}. {f.name}
                      </option>
                    ))}
                </select>
                <div className="field-hint">Select where this folder should appear in the tree</div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={() => setShowFolderModal(false)} disabled={folderActionLoading}>Cancel</button>
          {folderModalMode === 'delete' ? (
            <button className="btn btn-danger" onClick={handleFolderDelete} disabled={folderActionLoading}>
              <i className="ti ti-trash" /> Delete Folder
            </button>
          ) : (
            <button className="btn btn-primary" onClick={handleFolderSubmit} disabled={folderActionLoading}>
              <i className="ti ti-check" /> {folderModalMode === 'add' ? 'Create' : 'Save'} Folder
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (!study) {
    return <EmptyState icon="ti-folder-off" title="Study not found" />;
  }

  const statusVariant = (s) => ({
    approved: 'ok',
    review: 'warn',
    active: 'info',
    draft: 'default',
    superseded: 'default',
    archived: 'default'
  })[s] || 'default';

  const docCount = documentRows.filter((d) => !d.deleted).length;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'ti-info-circle' },
    { id: 'data', label: 'Data', icon: 'ti-database' },
    { id: 'nca', label: 'NCA', icon: 'ti-chart-bar' },
    { id: 'stats', label: 'Statistics', icon: 'ti-calculator' },
    { id: 'documents', label: 'Documents', icon: 'ti-folder', badge: docCount }
  ];

  const workflowSteps = [
    { label: 'Study Configuration', done: true },
    { label: 'Data Import & Validation', done: true },
    { label: 'NCA Derivation', done: true },
    { label: 'Statistical Analysis', done: study.status === 'In Analysis' || study.status === 'Reporting' },
    { label: 'Report Generation', done: study.status === 'Reporting' }
  ];

  return (
    <div>
      <div className="flex gap-2 center mb-3">
        <button className="btn btn-sm btn-ghost" onClick={onBack}>
          <i className="ti ti-arrow-left" /> Back to studies
        </button>
      </div>

      {/* Study Header */}
      <div className="page-header">
        <div>
          <div className="page-eyebrow">{study.code} · {study.studyLabel || '—'} · {study.phase} · {study.cro}</div>
          <h1 className="page-title" style={{ fontSize: 26 }}>{study.protocolTitle}</h1>
          <div className="flex gap-2 mt-2" style={{ flexWrap: 'wrap' }}>
            <Tag variant="ok" dot>{study.status}</Tag>
            <Tag>{study.molecule}</Tag>
            <Tag>{study.indication}</Tag>
            <Tag>{study.country}</Tag>
            <span className="xs muted">{fmtDate(study.plannedStart)} → {fmtDate(study.plannedEnd)}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="study-tabs">
        {tabs.map(t => (
          <button
            key={t.id}
            className={`study-tab ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            <i className={`ti ${t.icon}`} />
            {t.label}
            {t.badge !== undefined && (
              <span className="tab-badge">{t.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* ============ OVERVIEW TAB ============ */}
        {activeTab === 'overview' && (
          <div className="grid-split">
            <div>
              <div className="card card-pad mb-3">
                <div className="section-h"><h2>Study configuration</h2></div>
                <div className="grid-2">
                  <div>
                    <div className="text-mute text-small mb-4" style={{ fontWeight: 700 }}>Study ID</div>
                    <div className="mono" style={{ fontWeight: 400 }}>{study.studyLabel || '—'}</div>
                  </div>
                  <div>
                    <div className="text-mute text-small mb-4" style={{ fontWeight: 700 }}>Study code</div>
                    <div className="mono" style={{ fontWeight: 400 }}>{study.studyCode || study.code || '—'}</div>
                  </div>
                  <div>
                    <div className="text-mute text-small mb-4" style={{ fontWeight: 700 }}>Phase / Type</div>
                    <div style={{ fontWeight: 400 }}>{study.phaseType || study.phase || '—'}</div>
                  </div>
                  <div>
                    <div className="text-mute text-small mb-4" style={{ fontWeight: 700 }}>Protocol title</div>
                    <div style={{ fontWeight: 400 }}>{study.protocolTitle || '—'}</div>
                  </div>
                  <div>
                    <div className="text-mute text-small mb-4" style={{ fontWeight: 700 }}>Molecule / Product</div>
                    <div style={{ fontWeight: 400 }}>{study.moleculeProduct || study.molecule || '—'}</div>
                  </div>
                  <div>
                    <div className="text-mute text-small mb-4" style={{ fontWeight: 700 }}>Indication</div>
                    <div style={{ fontWeight: 400 }}>{study.indication || '—'}</div>
                  </div>
                  <div>
                    <div className="text-mute text-small mb-4" style={{ fontWeight: 700 }}>Design</div>
                    <div style={{ fontWeight: 400 }}>{study.design || '—'}</div>
                  </div>
                  <div>
                    <div className="text-mute text-small mb-4" style={{ fontWeight: 700 }}>Dose form</div>
                    <div style={{ fontWeight: 400 }}>{study.doseForm || '—'}</div>
                  </div>
                  <div>
                    <div className="text-mute text-small mb-4" style={{ fontWeight: 700 }}>CRO</div>
                    <div style={{ fontWeight: 400 }}>{study.cro || '—'}</div>
                  </div>
                  <div>
                    <div className="text-mute text-small mb-4" style={{ fontWeight: 700 }}>Country</div>
                    <div style={{ fontWeight: 400 }}>{study.country || '—'}</div>
                  </div>
                  <div>
                    <div className="flex gap-2 center" style={{ alignItems: 'center' }}>
                      <div className="text-mute text-small" style={{ fontWeight: 700 }}>Status</div>
                      <Tag variant={statusVariant(study.status)} dot>{study.status}</Tag>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card card-pad">
                <div className="section-h"><h2>Workflow progress</h2></div>
                {workflowSteps.map((s, i) => (
                  <div key={i} className="progress-step">
                    <div className={`step-icon ${s.done ? 'done' : 'pending'}`}>
                      {s.done ? <i className="ti ti-check" style={{ fontSize: 12 }} /> : i + 1}
                    </div>
                    <span className={`step-label ${s.done ? 'done' : 'pending'}`}>
                      {s.label}
                    </span>
                    {s.done && <span className="step-status">Complete</span>}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="stats-grid mb-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="stat">
                  <div className="stat-label">Subjects</div>
                  <div className="stat-value">{study.subjects || study.nSubjects || '—'}</div>
                </div>
                <div className="stat">
                  <div className="stat-label">Documents</div>
                  <div className="stat-value">{docCount}</div>
                  <div className="stat-sub">{documentRows.filter((d) => d.deleted).length} deleted</div>
                </div>
              </div>

              <div className="card card-pad mb-3">
                <div className="text-mute text-small mb-8" style={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600 }}>
                  Sponsor
                </div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{client?.name || '—'}</div>
                <div className="text-mute text-small">{client?.sponsor || ''}</div>
                <div className="divider"></div>
                <div className="text-mute text-small mb-4" style={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600 }}>
                  Contact
                </div>
                <div className="text-small mb-4">{client?.contact || '—'}</div>
                <div className="text-mute text-small mono">{client?.email || '—'}</div>
              </div>

              <div className="card card-pad">
                <div className="text-mute text-small mb-8" style={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600 }}>
                  Timeline
                </div>
                <div className="flex-between" style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <span className="text-small">Planned start: </span>
                  <span className="mono text-small">{fmtDate(study.plannedStart) || '—'}</span>
                </div>
                <div className="flex-between" style={{ padding: '6px 0' }}>
                  <span className="text-small">Planned end: </span>
                  <span className="mono text-small">{fmtDate(study.plannedEnd) || '—'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============ DATA TAB ============ */}
        {activeTab === 'data' && (
          <div>
            <div className="section-h">
              <div>
                <h2>Data Import & Validation</h2>
                <p className="text-mute text-small" style={{ margin: '4px 0 0' }}>Concentration data, BLQ handling, protocol checks</p>
              </div>
              <div className="flex gap-8">
                <button className="btn btn-ghost btn-sm">
                  <i className="ti ti-download" style={{ fontSize: 13 }} /> Export CSV
                </button>
                <button className="btn btn-primary btn-sm">
                  <i className="ti ti-upload" style={{ fontSize: 13 }} /> Import file
                </button>
              </div>
            </div>

            <div className="banner banner-pass">
              <i className="ti ti-check banner-icon" style={{ fontSize: 16 }} />
              <div>
                Validation passed — {docCount} documents, 0 missing. All subjects have both periods.
              </div>
            </div>

            <div className="tbl-wrap" style={{ maxHeight: 500, overflow: 'auto' }}>
              <table className="tbl tbl-tight">
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th>Document</th>
                    <th>Folder</th>
                    <th>Type</th>
                    <th>Version</th>
                    <th>Status</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {documentRows.filter((d) => !d.deleted).slice(0, 10).map((d) => (
                    <tr key={d.id}>
                      <td className="mono">{d.name}</td>
                      <td className="xs">{folderById[d.folder] ? `${folderById[d.folder].code}. ${folderById[d.folder].name}` : '—'}</td>
                      <td>{d.type}</td>
                      <td className="num">v{d.version}</td>
                      <td><Tag variant={statusVariant(d.status)} dot>{d.status === 'review' ? 'Under review' : d.status}</Tag></td>
                      <td className="xs muted">{relTime(d.updatedAt)}</td>
                    </tr>
                  ))}
                  {documentRows.filter((d) => !d.deleted).length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-mute" style={{ textAlign: 'center', padding: 40 }}>
                        <i className="ti ti-file-off" style={{ fontSize: 24, display: 'block', marginBottom: 8 }} />
                        No documents uploaded yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ============ NCA TAB ============ */}
        {activeTab === 'nca' && (
          <div>
            <div className="section-h">
              <div>
                <h2>Non-Compartmental Analysis</h2>
                <p className="text-mute text-small" style={{ margin: '4px 0 0' }}>Linear-up / log-down trapezoidal AUC · terminal λz by best-subset adj-R² regression</p>
              </div>
              <button className="btn btn-ghost btn-sm">
                <i className="ti ti-download" style={{ fontSize: 13 }} /> Export CSV
              </button>
            </div>

            <div className="tbl-wrap" style={{ maxHeight: 540, overflow: 'auto' }}>
              <table className="tbl tbl-tight">
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th>Subject</th>
                    <th>Per.</th>
                    <th>Trt</th>
                    <th className="num">Cmax</th>
                    <th className="num">Tmax</th>
                    <th className="num">AUC(0-t)</th>
                    <th className="num">AUC(0-∞)</th>
                    <th className="num">λz</th>
                    <th className="num">t½ (h)</th>
                    <th className="num">% Extrap</th>
                    <th className="num">adj-R²</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={11} className="text-mute" style={{ textAlign: 'center', padding: 40 }}>
                      <i className="ti ti-chart-bar" style={{ fontSize: 24, display: 'block', marginBottom: 8 }} />
                      NCA results will appear here after analysis
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="banner banner-info mt-3">
              <i className="ti ti-info-circle banner-icon" style={{ fontSize: 16 }} />
              <div>
                <strong>NCA Parameters:</strong> Cmax, Tmax, AUC(0-t), AUC(0-∞), λz, t½, %Extrap, adj-R²
              </div>
            </div>
          </div>
        )}

        {/* ============ STATISTICS TAB ============ */}
        {activeTab === 'stats' && (
          <div>
            <div className="section-h">
              <div>
                <h2>Statistical Analysis — Average Bioequivalence</h2>
                <p className="text-mute text-small" style={{ margin: '4px 0 0' }}>Log-transformed parameters · 90% CI for GMR · BE limits 80.00 – 125.00%</p>
              </div>
              <button className="btn btn-primary btn-sm">
                <i className="ti ti-play" style={{ fontSize: 13 }} /> Run analysis
              </button>
            </div>

            <div className="banner banner-pass">
              <i className="ti ti-check banner-icon" style={{ fontSize: 16 }} />
              <div>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>Bioequivalence ESTABLISHED</div>
                <div style={{ fontSize: 12, opacity: 0.85 }}>
                  All three parameters (Cmax, AUC(0-t), AUC(0-∞)) are within the 80.00 – 125.00% acceptance limits at 90% CI.
                </div>
              </div>
            </div>

            <div className="card card-pad mb-24">
              <div className="text-mute text-small mb-3" style={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600 }}>
                Submission-Ready BE Result Table
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="tbl tbl-tight" style={{ minWidth: 720 }}>
                  <thead>
                    <tr>
                      <th style={{ minWidth: 200 }}>Statistical Parameter</th>
                      <th className="num">Cmax</th>
                      <th className="num">AUC(0-t)</th>
                      <th className="num">AUC(0-∞)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td style={{ fontWeight: 600 }}>N (subjects)</td><td className="num">24</td><td className="num">24</td><td className="num">24</td></tr>
                    <tr><td>LSM Test (back-transformed)</td><td className="num">102.4</td><td className="num">100.8</td><td className="num">101.2</td></tr>
                    <tr><td>LSM Reference (back-transformed)</td><td className="num">99.8</td><td className="num">98.6</td><td className="num">99.4</td></tr>
                    <tr><td style={{ fontWeight: 600 }}>Geometric Mean Ratio (T/R)</td>
                      <td className="num" style={{ fontWeight: 700 }}>99.6 %</td>
                      <td className="num" style={{ fontWeight: 700 }}>100.2 %</td>
                      <td className="num" style={{ fontWeight: 700 }}>99.8 %</td>
                    </tr>
                    <tr><td style={{ fontWeight: 600 }}>90% Confidence Interval</td>
                      <td className="num" style={{ fontWeight: 600, color: 'var(--ok)' }}>91.1 – 108.8</td>
                      <td className="num" style={{ fontWeight: 600, color: 'var(--ok)' }}>90.2 – 106.3</td>
                      <td className="num" style={{ fontWeight: 600, color: 'var(--ok)' }}>89.8 – 105.9</td>
                    </tr>
                    <tr><td>Intra-subject CV%</td><td className="num">18.2 %</td><td className="num">15.4 %</td><td className="num">16.8 %</td></tr>
                    <tr><td>Power (post-hoc)</td><td className="num">94.6 %</td><td className="num">96.8 %</td><td className="num">95.2 %</td></tr>
                    <tr><td style={{ fontWeight: 700 }}>BE Decision</td>
                      <td><Tag variant="ok">PASS</Tag></td>
                      <td><Tag variant="ok">PASS</Tag></td>
                      <td><Tag variant="ok">PASS</Tag></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="banner banner-info mt-3">
              <i className="ti ti-info-circle banner-icon" style={{ fontSize: 16 }} />
              <div>
                <strong>Method:</strong> Period-difference (Chow & Liu 2008) on log-transformed PK parameters from 2×2 crossover design.
              </div>
            </div>
          </div>
        )}

        {/* ============ DOCUMENTS TAB - Last Tab with Full Tree ============ */}
        {activeTab === 'documents' && (
          <div className="split">

            {/* Right Panel - Study Tree */}
            <div className="split-right">
              <div className="flex between center mb-2">
                <div className="card-title" style={{ margin: 0 }}>
                  <i className="ti ti-folder" /> Study tree
                </div>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => openAddFolderModal()}
                  title="Add root folder"
                  disabled={showDeleted}
                >
                  <i className="ti ti-plus" /> Add Folder
                </button>
              </div>

              <div className="field" style={{ marginBottom: 8 }}>
                <input
                  type="text"
                  placeholder="Filter folders..."
                  value={filterQuery}
                  onChange={(e) => setFilterQuery(e.target.value)}
                  style={{ fontSize: 12, padding: '6px 10px', width: '100%', border: '1px solid var(--line)', borderRadius: 4 }}
                />
              </div>

              <div className="tree">
                {treeLoading ? (
                  <div className="xs muted" style={{ padding: '10px 8px' }}>Loading study tree...</div>
                ) : visibleFolders.length === 0 ? (
                  <div className="xs muted" style={{ padding: '10px 8px' }}>No folders found.</div>
                ) : (
                  visibleFolders
                    .filter((f) => !f.parentId || !visibleFolders.some((p) => p.uniqueId === f.parentId))
                    .map((rootFolder) => {
                      const getChildren = (parentUniqueId) => visibleFolders.filter((f) => f.parentId === parentUniqueId);

                      const renderTree = (folder, level = 0) => {
                        const isExpanded = expandedFolders.includes(folder.uniqueId);
                        const isActive = activeFolder === folder.uniqueId;
                        const count = folderCounts[folder.uniqueId] || 0;
                        const children = getChildren(folder.uniqueId);
                        const hasChildren = children.length > 0;

                        return (
                          <div key={folder.uniqueId}>
                            <div
                              className={`tree-node ${isActive ? 'active' : ''}`}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                paddingLeft: 8 + (level * 16),
                                borderRadius: 4,
                                cursor: 'pointer',
                                position: 'relative',
                              }}
                            >
                              {hasChildren && (
                                <span
                                  onClick={(e) => { e.stopPropagation(); toggleFolder(folder.uniqueId); }}
                                  style={{
                                    marginRight: 4,
                                    fontSize: 12,
                                    color: 'var(--ink-3)',
                                    cursor: 'pointer',
                                    width: 18,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <i className={`ti ${isExpanded ? 'ti-chevron-down' : 'ti-chevron-right'}`} style={{ fontSize: 12 }} />
                                </span>
                              )}

                              <span
                                onClick={() => { setActiveFolder(folder.uniqueId); setSelectedDocId(null); }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  flex: 1,
                                  padding: '4px 0'
                                }}
                              >
                                <i className={getFolderIcon(folder.uniqueId)} style={{ fontSize: 14 }} />
                                <span style={{ fontSize: 12.5 }}>
                                  {folder.code}. {folder.name}
                                </span>
                                <span className="count">{count > 0 ? count : ''}</span>
                              </span>

                              <span
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  display: 'flex',
                                  gap: 2,
                                  alignItems: 'center',
                                  opacity: 0.6,
                                  transition: 'opacity 0.15s',
                                  position: 'absolute',
                                  right: 5
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                                onMouseLeave={(e) => e.currentTarget.style.opacity = 0.6}
                              >
                                {!showDeleted && (
                                  <button
                                    className="icon-btn"
                                    onClick={() => openAddFolderModal(folder.uniqueId)}
                                    title="Add subfolder"
                                    style={{ width: 22, height: 22, fontSize: 12, border: 'none', color: 'var(--ink-3)' }}
                                  >
                                    <i className="ti ti-plus" />
                                  </button>
                                )}

                                {!showDeleted && (
                                  <button
                                    className="icon-btn"
                                    onClick={() => openEditFolderModal(folder)}
                                    title="Edit folder"
                                    style={{ width: 22, height: 22, fontSize: 12, border: 'none', color: 'var(--ink-3)' }}
                                  >
                                    <i className="ti ti-edit" />
                                  </button>
                                )}

                                {!showDeleted && (
                                  <button
                                    className="icon-btn"
                                    onClick={() => openDeleteFolderModal(folder)}
                                    title="Soft delete folder"
                                    style={{ width: 22, height: 22, fontSize: 12, border: 'none', color: 'var(--danger)' }}
                                  >
                                    <i className="ti ti-trash" />
                                  </button>
                                )}

                                {showDeleted && folder.deleted && (
                                  <button
                                    className="icon-btn"
                                    onClick={() => handleRestoreFolder(folder)}
                                    title="Restore folder"
                                    style={{ width: 22, height: 22, fontSize: 12, border: 'none', color: 'var(--ok)' }}
                                  >
                                    <i className="ti ti-restore" />
                                  </button>
                                )}

                                {showDeleted && folder.deleted && (
                                  <button
                                    className="icon-btn"
                                    onClick={() => handlePermanentDeleteFolder(folder)}
                                    title="Permanent delete folder"
                                    style={{ width: 22, height: 22, fontSize: 12, border: 'none', color: 'var(--danger)' }}
                                  >
                                    <i className="ti ti-trash-x" />
                                  </button>
                                )}
                              </span>
                            </div>

                            {hasChildren && isExpanded && (
                              <div>
                                {children.map((child) => renderTree(child, level + 1))}
                              </div>
                            )}
                          </div>
                        );
                      };

                      return renderTree(rootFolder);
                    })
                )}
              </div>

              <div className="divider" />

              <label className="flex gap-2 center xs" style={{ cursor: 'pointer', padding: '4px 8px' }}>
                <input
                  type="checkbox"
                  checked={showDeleted}
                  onChange={(e) => {
                    setShowDeleted(e.target.checked);
                    setSelectedDocId(null);
                    setPreviewDocId(null);
                  }}
                />
                <span>Show soft-deleted</span>
                <span className="muted">({countDeleted})</span>
              </label>
            </div>

            {/* Left Panel - Document List */}
            <div className="split-left" style={{ borderRight: '1px solid var(--line)' }}>
              {selectedDoc && !showDeleted ? (
                <DocumentDetailPanel
                  document={selectedDoc}
                  onClose={() => setSelectedDocId(null)}
                  onUpdated={getStudyFolderAndDocumentData}
                />
              ) : (
                <>
                  <div className="flex between center mb-3">
                    <div>
                      <div className="bold sm">
                        {showDeleted ? 'Soft-deleted documents' : (activeFolderNode?.name || activeFolderNode?.code || '—')}
                      </div>
                      <div className="xs muted">
                        {showDeleted ? 'All deleted documents across all folders' : (activeFolderNode?.desc || '')}
                      </div>
                    </div>
                    <button className="btn btn-sm btn-primary" onClick={() => setShowUpload(true)} disabled={!activeFolderNode || showDeleted}>
                      <i className="ti ti-upload" /> Upload
                    </button>
                  </div>

                  {(showDeleted ? deletedDocuments.length === 0 : docsInFolder.length === 0) ? (
                    <EmptyState
                      icon="ti-file-off"
                      title="No documents"
                      desc={showDeleted ? 'No soft-deleted documents found.' : 'Upload documents to populate this folder.'}
                      action={!showDeleted && (
                        <button className="btn btn-sm btn-primary" onClick={() => setShowUpload(true)}>
                          <i className="ti ti-upload" /> Upload
                        </button>
                      )}
                    />
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table className="tbl">
                        <thead>
                          <tr>
                            <th>Document</th>
                            <th>Folder</th>
                            <th>Version</th>
                            <th>Status</th>
                            <th>{showDeleted ? 'Deleted On' : 'Updated'}</th>
                            <th style={{ width: 80 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(showDeleted ? deletedDocuments : docsInFolder).map((d) => (
                            <tr
                              key={d.id}
                              onClick={() => !d.deleted && setSelectedDocId(d.id)}
                              style={{
                                cursor: d.deleted ? 'default' : 'pointer',
                                opacity: d.deleted ? 0.55 : 1
                              }}
                            >
                              <td>
                                <div className="flex gap-2 center">
                                  <i className={`ti ${d.name.endsWith('.pdf') ? 'ti-file-type-pdf' : d.name.endsWith('.docx') ? 'ti-file-type-doc' : d.name.endsWith('.xlsx') ? 'ti-file-type-xls' : 'ti-file'}`} style={{ fontSize: 16, color: 'var(--ink-3)' }} />
                                  <div>
                                    <div className="medium">{d.name}</div>
                                    <div className="xs muted">{d.type} · {d.size}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="xs">{getDocumentFolderLabel(d)}</td>
                              <td className="num">v{d.version}</td>
                              <td>
                                {d.deleted ? (
                                  <Tag>Deleted</Tag>
                                ) : (
                                  <Tag variant={statusVariant(d.status)} dot>
                                    {d.status === 'review' ? 'Under review' : d.status}
                                  </Tag>
                                )}
                              </td>
                              <td className="xs muted">{showDeleted ? (d.deletedDT ? relTime(d.deletedDT) : '—') : relTime(d.updatedAt)}</td>
                              <td onClick={(e) => e.stopPropagation()}>
                                {showDeleted ? (
                                  <div className="flex gap-1">
                                    <button className="btn btn-xs" onClick={() => handleRestore(d)}>
                                      <i className="ti ti-restore" /> Restore
                                    </button>
                                    <button className="btn btn-xs" onClick={() => handlePermanentDeleteDocument(d)}>
                                      <i className="ti ti-trash-x" /> Delete
                                    </button>
                                  </div>
                                ) : d.deleted ? (
                                  <button className="btn btn-xs" onClick={() => handleRestore(d)}>
                                    <i className="ti ti-restore" /> Restore
                                  </button>
                                ) : (
                                  <div className="flex gap-1">
                                    <button
                                      className="icon-btn"
                                      onClick={(e) => { e.stopPropagation(); setPreviewDocId(d.id); }}
                                      title="Preview"
                                      style={{ width: 26, height: 26 }}
                                    >
                                      <i className="ti ti-eye" style={{ fontSize: 13 }} />
                                    </button>
                                    <i className="ti ti-chevron-right muted" style={{ alignSelf: 'center' }} />
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

          </div>
        )}
      </div>

      <DocumentUploadModal
        open={showUpload}
        onClose={() => setShowUpload(false)}
        studyId={resolvedStudyId}
        currentFolderId={activeFolderNode?.uniqueId || ''}
        folderOptions={allFolders.filter((f) => !f.deleted)}
        onUploaded={getStudyFolderAndDocumentData}
      />
      <DocumentPreviewModal open={!showDeleted && !!previewDoc} onClose={() => setPreviewDocId(null)} document={previewDoc} />

      {/* Folder Management Modal */}
      {renderFolderModal()}

      <style>{`
        .tree-node .icon-btn {
          opacity: 0;
          transition: opacity 0.15s;
        }
        .tree-node:hover .icon-btn {
          opacity: 1;
        }
        .tree-node .icon-btn {
          border: none;
          background: transparent;
          padding: 2px;
          cursor: pointer;
          border-radius: 4px;
        }
        .tree-node .icon-btn:hover {
          background: var(--surface-2);
        }
        .tree-node .icon-btn .ti-plus {
          color: var(--accent-3);
        }
        .tree-node .icon-btn .ti-edit {
          color: var(--info);
        }
        .tree-node .icon-btn .ti-trash {
          color: var(--danger);
        }
        .btn-danger {
          background: var(--danger);
          border-color: var(--danger);
          color: #ffffff;
        }
        .btn-danger:hover {
          background: #b91c1c;
          border-color: #b91c1c;
        }
        .alert.warn {
          background: var(--warn-soft);
          border: 1px solid var(--warn);
          color: var(--warn);
        }
      `}</style>
    </div>
  );
}

export default StudyDetailPage;